import { z } from 'zod';
import { zodHelpers as sharedZodHelpers } from '@axon/common';
import type { Path, UseFormSetError, Resolver, FieldValues } from 'react-hook-form';
import { zodResolver as hookFormZodResolver } from '@hookform/resolvers/zod';

// Common validation schemas
export const validationSchemas = {
  email: z.string().email('Geçerli bir e-posta adresi girin'),

  phone: z
    .string()
    .regex(
      /^(\+90[1-9][0-9]{9}|0[1-9][0-9]{9}|[5][0-9]{9})$/,
      'Geçerli bir telefon numarası girin'
    ),

  tcKimlik: z
    .string()
    .length(11, 'TC Kimlik No 11 haneli olmalıdır')
    .regex(/^[1-9][0-9]{10}$/, 'Geçerli bir TC Kimlik No girin')
    .refine((value) => validators.tcKimlikNo(value), 'Geçersiz TC Kimlik No'),

  vergiNo: z
    .string()
    .length(10, 'Vergi No 10 haneli olmalıdır')
    .regex(/^[0-9]{10}$/, 'Geçerli bir Vergi No girin'),

  iban: z.string().regex(/^TR[0-9]{2}[0-9]{5}[0-9A-Z]{17}$/, 'Geçerli bir IBAN girin'),

  plateNumber: z
    .string()
    .regex(/^[0-9]{2}[A-Z]{1,3}[0-9]{2,4}$/, 'Geçerli bir plaka girin (örn: 34ABC123)'),

  url: z.string().url('Geçerli bir URL girin'),

  postalCode: z.string().regex(/^[0-9]{5}$/, 'Geçerli bir posta kodu girin'),

  percentage: z.number().min(0, "Yüzde 0'dan küçük olamaz").max(100, "Yüzde 100'den büyük olamaz"),

  positiveNumber: z.number().positive('Pozitif bir sayı girin'),

  nonNegativeNumber: z.number().nonnegative('Negatif sayı girilemez'),

  // Date validations
  futureDate: z.date().refine((date) => date > new Date(), 'Gelecek bir tarih seçmelisiniz'),

  pastDate: z.date().refine((date) => date < new Date(), 'Geçmiş bir tarih seçmelisiniz'),

  dateInRange: (min: Date, max: Date) =>
    z
      .date()
      .refine(
        (date) => date >= min && date <= max,
        `Tarih ${min.toLocaleDateString('tr-TR')} ile ${max.toLocaleDateString('tr-TR')} arasında olmalıdır`
      ),

  // Common string patterns
  username: z
    .string()
    .min(3, 'Kullanıcı adı en az 3 karakter olmalıdır')
    .max(20, 'Kullanıcı adı en fazla 20 karakter olabilir')
    .regex(/^[a-zA-Z0-9_-]+$/, 'Kullanıcı adı sadece harf, rakam, tire ve alt çizgi içerebilir'),

  alphanumeric: z.string().regex(/^[a-zA-Z0-9]+$/, 'Sadece harf ve rakam kullanabilirsiniz'),

  numeric: z.string().regex(/^[0-9]+$/, 'Sadece rakam kullanabilirsiniz'),
};

// ============================================================
// Simple Validation Utilities (No Zod dependency)
// ============================================================

/**
 * Email validation regex patterns
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * E.164 international phone number format regex
 * Matches: +1234567890, 1234567890 (1-15 digits, optionally prefixed with +)
 */
export const PHONE_E164_REGEX = /^\+?[1-9]\d{1,14}$/;

/**
 * Simple validation utilities for inline use (no Zod required)
 */
export const SimpleValidators = {
  /**
   * Validates email format
   * @param email - Email string to validate
   * @returns true if valid, false otherwise
   */
  isValidEmail: (email: string): boolean => {
    if (!email || typeof email !== 'string') return false;
    return EMAIL_REGEX.test(email.trim());
  },

  /**
   * Validates E.164 international phone format
   * @param phone - Phone number string (spaces are stripped)
   * @returns true if valid, false otherwise
   */
  isValidPhoneE164: (phone: string): boolean => {
    if (!phone || typeof phone !== 'string') return false;
    return PHONE_E164_REGEX.test(phone.replace(/\s/g, ''));
  },

  /**
   * Validates required field (non-empty after trim)
   * @param value - Value to check
   * @returns true if value is present and non-empty
   */
  isRequired: (value: string | undefined | null): boolean => {
    if (value === undefined || value === null) return false;
    return typeof value === 'string' && value.trim().length > 0;
  },

  /**
   * Validates minimum length
   * @param value - String value to check
   * @param min - Minimum length
   * @returns true if value meets minimum length
   */
  hasMinLength: (value: string | undefined | null, min: number): boolean => {
    if (!value) return min === 0;
    return value.length >= min;
  },

  /**
   * Validates maximum length
   * @param value - String value to check
   * @param max - Maximum length
   * @returns true if value is within maximum length
   */
  hasMaxLength: (value: string | undefined | null, max: number): boolean => {
    if (!value) return true;
    return value.length <= max;
  },

  /**
   * Validates array has minimum items
   * @param arr - Array to validate
   * @param min - Minimum number of items
   * @returns true if array has at least min items
   */
  hasMinItems: <T>(arr: T[] | undefined | null, min: number): boolean => {
    if (!arr || !Array.isArray(arr)) return min === 0;
    return arr.length >= min;
  },
};

// Custom validators
export const validators = {
  // TC Kimlik No validation with checksum
  tcKimlikNo: (value: string) => {
    if (!value || value.length !== 11) return false;

    const digits = value.split('').map(Number);
    if (digits[0] === 0) return false;

    const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
    const evenSum = digits[1] + digits[3] + digits[5] + digits[7];

    const tenthDigit = (oddSum * 7 - evenSum) % 10;
    const eleventhDigit = (oddSum + evenSum + digits[9]) % 10;

    return digits[9] === tenthDigit && digits[10] === eleventhDigit;
  },

  // IBAN validation with checksum
  iban: (value: string) => {
    if (!value || !value.startsWith('TR') || value.length !== 26) return false;

    // Move first 4 chars to end and convert to numbers
    const rearranged = value.substring(4) + value.substring(0, 4);
    const numericString = rearranged.replace(/[A-Z]/g, (char) =>
      (char.charCodeAt(0) - 55).toString()
    );

    // Modulo 97 check
    let remainder = 0;
    for (let i = 0; i < numericString.length; i++) {
      remainder = (remainder * 10 + parseInt(numericString[i])) % 97;
    }

    return remainder === 1;
  },

  // Credit card validation (Luhn algorithm)
  creditCard: (value: string) => {
    const digits = value.replace(/\s/g, '');
    if (!/^[0-9]{13,19}$/.test(digits)) return false;

    let sum = 0;
    let isEven = false;

    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = parseInt(digits[i]);

      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  },

  // Strong password validation
  strongPassword: (value: string) => {
    const hasLowerCase = /[a-z]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const isLongEnough = value.length >= 8;

    return {
      isValid: hasLowerCase && hasUpperCase && hasNumber && hasSpecialChar && isLongEnough,
      criteria: {
        hasLowerCase,
        hasUpperCase,
        hasNumber,
        hasSpecialChar,
        isLongEnough,
      },
    };
  },
};

// Async validators
export const asyncValidators = {
  // Check if email is unique
  checkEmailUnique: async (email: string): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    // In real app, this would call the API
    const existingEmails = ['admin@tms.com', 'user@tms.com'];
    return !existingEmails.includes(email.toLowerCase());
  },

  // Check if username is available
  checkUsernameAvailable: async (username: string): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 300));

    // In real app, this would call the API
    const takenUsernames = ['admin', 'user', 'test'];
    return !takenUsernames.includes(username.toLowerCase());
  },

  // Validate address with external service
  validateAddress: async (address: {
    street: string;
    city: string;
    postalCode: string;
  }): Promise<{ isValid: boolean; suggestions?: string[] }> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 800));

    // In real app, this would call address validation API
    return {
      isValid: address.postalCode.length === 5,
      suggestions: address.postalCode.length !== 5 ? ['Posta kodu 5 haneli olmalıdır'] : undefined,
    };
  },
};

// Cross-field validation helpers
export const crossFieldValidators = {
  // Date range validation
  dateRange: (startDate: Date, endDate: Date) => {
    return startDate <= endDate;
  },

  // Password confirmation
  passwordMatch: (password: string, confirmPassword: string) => {
    return password === confirmPassword;
  },

  // Conditional required field
  conditionalRequired: (value: unknown, condition: boolean) => {
    if (condition) {
      return !!value;
    }
    return true;
  },

  // Sum validation (e.g., percentages must sum to 100)
  sumValidation: (values: number[], expectedSum: number) => {
    const sum = values.reduce((acc, val) => acc + val, 0);
    return Math.abs(sum - expectedSum) < 0.01; // Allow small floating point differences
  },
};

// Form-level validation helper
export function createFormValidator<T extends Record<string, unknown>>(
  validations: Array<(data: T) => { field: Path<T>; message: string } | null>
) {
  return (data: T, setError: UseFormSetError<T>) => {
    let isValid = true;

    validations.forEach((validation) => {
      const error = validation(data);
      if (error) {
        setError(error.field, { message: error.message });
        isValid = false;
      }
    });

    return isValid;
  };
}

// Validation message helpers
export const validationMessages = {
  required: (fieldName: string) => `${fieldName} zorunludur`,
  minLength: (fieldName: string, min: number) => `${fieldName} en az ${min} karakter olmalıdır`,
  maxLength: (fieldName: string, max: number) => `${fieldName} en fazla ${max} karakter olabilir`,
  min: (fieldName: string, min: number) => `${fieldName} ${min}'dan küçük olamaz`,
  max: (fieldName: string, max: number) => `${fieldName} ${max}'dan büyük olamaz`,
  pattern: (fieldName: string) => `${fieldName} geçerli bir formatta değil`,
  email: () => 'Geçerli bir e-posta adresi girin',
  url: () => 'Geçerli bir URL girin',
  custom: (message: string) => message,
};

// Canonical helpers live in `@axon/common/schemas/zod-helpers` so both
// frontend RHF forms and backend zod validators share one source. Re-export
// the shared set and add `turkishEnum` as a thin wrapper over
// `localizedEnum` for the existing `zodHelpers.turkishEnum(...)` callers.
export const zodHelpers = {
  ...sharedZodHelpers,
  turkishEnum: <T extends string>(values: readonly T[] & [T, ...T[]], fieldName: string) =>
    sharedZodHelpers.localizedEnum(
      values,
      fieldName,
      (f, v) => `${f} şu değerlerden biri olmalıdır: ${v.join(', ')}`
    ),
};

// Progressive validation helper
export class ProgressiveValidator<T> {
  private validations: Map<keyof T, () => boolean> = new Map();
  private touchedFields: Set<keyof T> = new Set();

  addValidation(field: keyof T, validation: () => boolean) {
    this.validations.set(field, validation);
    return this;
  }

  touch(field: keyof T) {
    this.touchedFields.add(field);
    return this;
  }

  validate(field: keyof T) {
    if (!this.touchedFields.has(field)) return true;

    const validation = this.validations.get(field);
    return validation ? validation() : true;
  }

  validateAll() {
    let isValid = true;

    this.validations.forEach((validation, field) => {
      if (this.touchedFields.has(field) && !validation()) {
        isValid = false;
      }
    });

    return isValid;
  }

  reset() {
    this.touchedFields.clear();
  }
}

// Composable field builders
export const fieldBuilders = {
  // Email field with optional custom message
  email: (message?: string) => z.string().email(message || validationMessages.email()),

  // Phone field with Turkish format
  phone: (required = false) => {
    const schema = z
      .string()
      .regex(/^(\+90|0)?[1-9][0-9]{9}$/, 'Geçerli bir telefon numarası girin');
    return required ? schema : schema.optional();
  },

  // TCKN field with full validation
  tcKimlik: (required = false) => {
    const schema = z
      .string()
      .length(11, 'TC Kimlik No 11 haneli olmalıdır')
      .regex(/^[1-9][0-9]{10}$/, 'Geçerli bir TC Kimlik No girin')
      .refine((value) => validators.tcKimlikNo(value), 'Geçersiz TC Kimlik No');
    return required ? schema : schema.optional();
  },

  // Tax number field
  taxNumber: (required = false) => {
    const schema = z
      .string()
      .length(10, 'Vergi No 10 haneli olmalıdır')
      .regex(/^[0-9]{10}$/, 'Geçerli bir Vergi No girin');
    return required ? schema : schema.optional();
  },

  // Date range fields
  dateRange: (fieldNames = { start: 'startDate', end: 'endDate' }) => ({
    [fieldNames.start]: z.date({
      message: 'Başlangıç tarihi zorunludur ve geçerli bir tarih olmalıdır',
    }),
    [fieldNames.end]: z.date({
      message: 'Bitiş tarihi zorunludur ve geçerli bir tarih olmalıdır',
    }),
  }),

  // Required text field
  requiredText: (fieldName: string, min = 1, max?: number) => {
    let schema = z.string().min(min, validationMessages.required(fieldName));
    if (max) {
      schema = schema.max(max, validationMessages.maxLength(fieldName, max));
    }
    return schema;
  },

  // Optional text field
  optionalText: (max?: number) => {
    let schema = zodHelpers.optionalString();
    if (max) {
      schema = z
        .string()
        .max(max, `En fazla ${max} karakter olabilir`)
        .trim()
        .transform((val) => (val === '' ? undefined : val))
        .optional();
    }
    return schema;
  },

  // URL field
  url: (required = false) => {
    const schema = z.string().url(validationMessages.url());
    return required ? schema : schema.optional();
  },

  // Number field with range
  numberInRange: (min: number, max: number, fieldName: string) =>
    z
      .number({
        message: validationMessages.required(fieldName),
      })
      .min(min, validationMessages.min(fieldName, min))
      .max(max, validationMessages.max(fieldName, max)),

  // Percentage field
  percentage: () => validationSchemas.percentage,
};

// Form schema factory
export function createFormSchema<T extends Record<string, unknown>>(
  fields: {
    [K in keyof T]: z.ZodType<T[K]>;
  },
  refinements?: Array<(data: T) => boolean | { path: (keyof T)[]; message: string }>
) {
  let schema = z.object(fields);

  if (refinements) {
    refinements.forEach((refinement) => {
      schema = schema.refine(
        (data) => {
          const result = refinement(data as T);
          return typeof result === 'boolean' ? result : false;
        },
        {
          message: 'Validation failed',
          path: [],
        }
      );
    });
  }

  return schema;
}

// Common form schemas
export const commonSchemas = {
  // Login form schema
  login: createFormSchema({
    email: fieldBuilders.email(),
    password: z.string().min(6, 'Şifre en az 6 karakter olmalıdır'),
    rememberMe: z.boolean().optional(),
  }),

  // Customer form schema
  customer: createFormSchema({
    name: fieldBuilders.requiredText('İsim'),
    companyName: fieldBuilders.requiredText('Firma adı'),
    email: fieldBuilders.email(),
    phone: fieldBuilders.phone(false),
    address: fieldBuilders.requiredText('Adres'),
    type: z.enum(['corporate', 'individual'], {
      message: 'Müşteri tipi seçiniz',
    }),
    taxNumber: fieldBuilders.taxNumber(false),
    taxOffice: fieldBuilders.optionalText(50),
  }),

  // Driver form schema
  driver: createFormSchema({
    firstName: fieldBuilders.requiredText('Ad'),
    lastName: fieldBuilders.requiredText('Soyad'),
    identityNumber: fieldBuilders.tcKimlik(true),
    phone: fieldBuilders.phone(true),
    birthDate: z.date({
      message: 'Doğum tarihi zorunludur',
    }),
    licenseNumber: z.string().min(1, 'Ehliyet numarası zorunludur'),
    licenseClass: z.enum(['B', 'C', 'CE', 'D', 'DE'], {
      message: 'Ehliyet sınıfı seçiniz',
    }),
  }),

  // Order form schema with date validation - manually creating the schema
  order: (() => {
    const baseSchema = z.object({
      customerId: z.string().min(1, 'Müşteri seçimi zorunludur'),
      pickupLocation: fieldBuilders.requiredText('Alım yeri'),
      deliveryLocation: fieldBuilders.requiredText('Teslim yeri'),
      pickupDate: z.date({
        message: 'Alım tarihi zorunludur ve geçerli bir tarih olmalıdır',
      }),
      deliveryDate: z.date({
        message: 'Teslim tarihi zorunludur ve geçerli bir tarih olmalıdır',
      }),
      cargo: fieldBuilders.requiredText('Kargo'),
      weight: fieldBuilders.numberInRange(0.1, 100000, 'Ağırlık'),
      quantity: z.number().int().positive('Adet pozitif bir tam sayı olmalıdır'),
      price: z.number().positive('Fiyat pozitif olmalıdır'),
      specialInstructions: fieldBuilders.optionalText(500),
    });

    return baseSchema.refine(
      (data) => {
        if (data.pickupDate && data.deliveryDate) {
          return data.deliveryDate >= data.pickupDate;
        }
        return true;
      },
      {
        message: 'Teslim tarihi alım tarihinden önce olamaz',
        path: ['deliveryDate'],
      }
    );
  })(),
};

/**
 * Zod v4 compatible resolver for React Hook Form
 * Handles the type compatibility issues between Zod v4 and @hookform/resolvers
 */
export function zodResolver<TFieldValues extends FieldValues = FieldValues, TContext = unknown>(
  schema: z.ZodSchema<TFieldValues>,
  schemaOptions?: Parameters<typeof hookFormZodResolver>[1],
  resolverOptions?: Parameters<typeof hookFormZodResolver>[2]
): Resolver<TFieldValues, TContext> {
  // Type assertion to make Zod v4 schema compatible with the resolver
  const compatibleSchema = schema as unknown as Parameters<typeof hookFormZodResolver>[0];

  // Handle overload resolution by calling with explicit parameters
  if (resolverOptions) {
    return hookFormZodResolver(compatibleSchema, schemaOptions, resolverOptions) as Resolver<
      TFieldValues,
      TContext
    >;
  } else if (schemaOptions) {
    return hookFormZodResolver(compatibleSchema, schemaOptions) as Resolver<TFieldValues, TContext>;
  } else {
    return hookFormZodResolver(compatibleSchema) as Resolver<TFieldValues, TContext>;
  }
}

// Export all utilities
export default {
  schemas: validationSchemas,
  validators,
  asyncValidators,
  crossFieldValidators,
  createFormValidator,
  messages: validationMessages,
  zodHelpers,
  ProgressiveValidator,
  fieldBuilders,
  createFormSchema,
  commonSchemas,
  zodResolver,
  // Simple validators (no Zod dependency)
  SimpleValidators,
  EMAIL_REGEX,
  PHONE_E164_REGEX,
};
